// Path matching for the SPA. A GitHub pull request lives under `/gh` followed
// by its path on github.com, so swapping `github.com` for `<host>/gh` in a PR
// URL lands on the same PR (see docs/design.md, "URLs"). The prefix names the
// forge; the rest of the address space is the app's own. A subject's URL
// mirrors GitHub's page of it (`subjectPath`, `parseSubjectPath`), and
// each routes to its page: a branch history's as the locator its URL
// names, which the page resolves against the repository.
import { compareHead, isGitHubName, isRefName, isRefPath, type CompareSubject, type PullSubject, type RefLocator, type RefSubject, type Subject, type SubjectKind } from './subject';

export type Route =
  | { name: 'dashboard' }
  /** The repository index; its owner view (`?owner=`) and Branches view (`?view=branches`) are its query's. */
  | { name: 'repos' }
  /** A repository's page, on one of its tabs (`/gh/o/r`, `/gh/o/r/branches`; `/gh/o/r/pulls` shows the first, see `repoAlias`). */
  | RepoRoute
  /**
   * A view of a pull request: `overview`, `files`, `commits` (Snapshots), or
   * `review` (the viewer's unsent drafts, sending them, and everything said
   * in review).
   */
  | PrRoute
  /** The signed-in user's own settings. */
  | { name: 'settings' }
  | { name: 'org-settings'; org: string }
  /** Feedback reports, for the deployment's operators. */
  | { name: 'feedback' }
  | { name: 'feedback-report'; id: number }
  /** A compare of two branches, on one of its views (Files, Commits). */
  | CompareRoute
  /** A branch history, on one of its views (Commits, Files): the locator its URL names, which its page resolves. */
  | RefRoute
  | { name: 'not-found'; path: string };

/** The repository page's tabs: its open pull requests (the page's own path) and its branches. */
export type RepoTab = 'pulls' | 'branches';

export interface RepoRoute {
  name: 'repo';
  owner: string;
  repo: string;
  tab: RepoTab;
}

export interface PrRoute {
  name: PrTab;
  subject: PullSubject;
}

export interface CompareRoute {
  name: 'compare';
  subject: CompareSubject;
  view: SubjectViews['compare'];
}

export interface RefRoute {
  name: 'ref';
  locator: RefLocator;
  view: SubjectViews['ref'];
}

/** Server-owned prefixes the SPA never handles. */
export const RESERVED_PREFIXES = ['/api/', '/auth/', '/webhooks/', '/assets/'];

/** Server-rendered pages (exact path or below it); links to them load the full page. */
export const SERVER_PAGES = ['/setup'];

function isServerPath(pathname: string): boolean {
  return (
    RESERVED_PREFIXES.some((p) => pathname.startsWith(p)) ||
    SERVER_PAGES.some((p) => pathname === p || pathname.startsWith(p + '/'))
  );
}

/** The prefix of every route that addresses a GitHub repository. */
export const GH = '/gh';

const NAME = '[A-Za-z0-9_.-]+';
const REPO = new RegExp(`^${GH}/(${NAME})/(${NAME})(?:/(pulls|branches))?/?$`);
const SETTINGS = /^\/settings\/?$/;
const ORG = new RegExp(`^/settings/orgs/(${NAME})/?$`);
const FEEDBACK = /^\/feedback(?:\/(\d+))?\/?$/;

/** The route of a path and its query (`search`, which names a compare's or a ref's view). */
export function matchRoute(pathname: string, search = ''): Route {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  if (path === '/' || path === '') return { name: 'dashboard' };
  if (path === GH || path === GH + '/') return { name: 'repos' };
  const at = parseSubjectPath(pathname, search);
  if (at && 'subject' in at) return at.subject.kind === 'pull' ? { name: at.view as PrTab, subject: at.subject } : { name: 'compare', subject: at.subject, view: at.view as SubjectViews['compare'] };
  if (at) return { name: 'ref', locator: at.locator, view: at.view };
  let m = REPO.exec(path);
  if (m) return { name: 'repo', owner: m[1], repo: m[2], tab: m[3] === 'branches' ? 'branches' : 'pulls' };
  if (SETTINGS.test(path)) return { name: 'settings' };
  m = ORG.exec(path);
  if (m) return { name: 'org-settings', org: m[1] };
  m = FEEDBACK.exec(path);
  if (m) {
    if (m[1] === undefined) return { name: 'feedback' };
    const id = Number(m[1]);
    if (Number.isSafeInteger(id) && id > 0) return { name: 'feedback-report', id };
  }
  return { name: 'not-found', path };
}

const SHA = '[0-9a-fA-F]{7,40}';
const PR_ALIAS = new RegExp(
  `^${GH}/(${NAME})/(${NAME})/pull/(\\d+)/(?:(?:commits|files|changes)/(${SHA})|(?:files|changes)/(${SHA})\\.\\.\\.?(${SHA})|(checks))/?$`,
);

/** Suffix of a `from` value naming the parent of a commit (`<sha>^`), as git writes it. */
export const PARENT = '^';

const TREE = new RegExp(`^${GH}/(${NAME})/(${NAME})/tree/(.+?)/?$`);

/**
 * The changelist URL for one of GitHub's other URLs, which a host swap
 * may bring here, or null when `pathname` is not one: a branch's tree
 * page, `/tree/<ref>[/<path>]`, is its history (`historyPath`, the page
 * resolving where the ref ends); and the pull request URLs
 * `/pull/N/commits/<sha>` and `/pull/N/files/<sha>` (one commit: the Files
 * view from its parent, `from=<sha>^`, to it), `/pull/N/files/<a>..<b>` (a
 * commit range: `from=<a>&to=<b>`), and `/pull/N/checks` (the Overview's
 * Checks card, `focus=checks`). `search` is the URL's query (kept, with these
 * parameters set).
 */
export function githubAlias(pathname: string, search = ''): string | null {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  const tree = TREE.exec(path);
  if (tree) {
    const history = historyPath(tree[1], tree[2], tree[3]);
    return history && history + search;
  }
  const m = PR_ALIAS.exec(path);
  if (!m) return null;
  const [, owner, repo, n, one, a, b, checks] = m;
  const number = Number(n);
  if (!Number.isSafeInteger(number) || number <= 0) return null;
  const q = new URLSearchParams(search);
  let target: string;
  if (checks) {
    target = prPath(owner, repo, number);
    q.set('focus', 'checks'); // headerTargets' FOCUS_PARAM
  } else {
    target = prPath(owner, repo, number, 'files');
    q.set('from', one ? one.toLowerCase() + PARENT : a.toLowerCase());
    q.set('to', (one ?? b).toLowerCase());
  }
  const qs = q.toString();
  return target + (qs ? '?' + qs : '');
}

const FORMER = new RegExp(`^${GH}/(${NAME})/(${NAME})/pull/(\\d+)/(?:drafts|publish)/?$`);

/**
 * The URL of the Review view for a URL of one of the two views it
 * replaced (`/pull/N/drafts`, `/pull/N/publish`), or null when `pathname`
 * is neither: a link or bookmark to either lands on the same pull request's
 * Review view, with its query kept, and the address bar says so.
 */
export function formerAlias(pathname: string, search = ''): string | null {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  const m = FORMER.exec(path);
  if (!m) return null;
  const number = Number(m[3]);
  if (!Number.isSafeInteger(number) || number <= 0) return null;
  return prPath(m[1], m[2], number, 'review') + search;
}

const FORMER_PULLS = new RegExp(`^${GH}/(${NAME})/(${NAME})/pulls/?$`);

/**
 * The repository page's URL for its former one, `/gh/o/r/pulls` (its pull
 * requests, which the page now opens on), or null for any other path: a
 * link or bookmark to it lands on the page, with its query kept, and the
 * address bar says so.
 */
export function repoAlias(pathname: string, search = ''): string | null {
  let path: string;
  try {
    path = decodeURI(pathname);
  } catch {
    path = pathname;
  }
  const m = FORMER_PULLS.exec(path);
  return m ? repoPath(m[1], m[2]) + search : null;
}

/** The repository page's path, on `tab` (its pull requests, the default, at the page's own path). */
export function repoPath(owner: string, repo: string, tab: RepoTab = 'pulls'): string {
  return `${GH}/${owner}/${repo}${tab === 'pulls' ? '' : '/' + tab}`;
}

/** A pull request's views, named as their routes name them (`commits` is Snapshots). */
export type PrTab = 'overview' | 'files' | 'commits' | 'review';

/** The views each kind of subject has; the first is where its URL without a view lands. */
export interface SubjectViews {
  pull: PrTab;
  compare: 'files' | 'commits';
  ref: 'commits' | 'files';
}
export type SubjectView<K extends SubjectKind = SubjectKind> = SubjectViews[K];

/** A subject and one of its views, as a URL names them. */
export type SubjectAt = { [K in SubjectKind]: { subject: Extract<Subject, { kind: K }>; view: SubjectViews[K] } }[SubjectKind];

/** What a URL names: a subject and its view, or (a GitHub `commits/…` URL) a ref locator to resolve and its view. */
export type Located = Exclude<SubjectAt, { subject: { kind: 'ref' } }> | { locator: RefLocator; view: SubjectViews['ref'] };

/** The repository index's path: one owner's repositories (`owner`), or the repositories with your branches without a pull request (`branches`). */
export function repoIndexPath(opts: { owner?: string | null; view?: 'branches' | null } = {}): string {
  const q = new URLSearchParams();
  if (opts.owner) q.set('owner', opts.owner);
  if (opts.view) q.set(VIEW_PARAM, opts.view);
  const qs = q.toString();
  return GH + (qs ? '?' + qs : '');
}

export function prPath(owner: string, repo: string, number: number, tab: '' | PrTab = ''): string {
  return `${GH}/${owner}/${repo}/pull/${number}${tab && tab !== 'overview' ? '/' + tab : ''}`;
}

/** A branch name (or a ref spec) in a path: percent-encoded, but for its slashes, so `feat/x` reads as on GitHub. */
function refPath(ref: string): string {
  return encodeURIComponent(ref).replace(/%2F/gi, '/');
}

/** The query parameter naming a compare's or a ref's view, written only off its default. */
export const VIEW_PARAM = 'view';

/**
 * The query parameter naming a history's path apart from its ref, so its
 * URL says where the ref ends (`subjectPath`). Not `path`, which the
 * Files view's filter is.
 */
export const UNDER_PARAM = 'under';

/**
 * The path of a subject's view, as GitHub's own page of it with
 * `github.com` swapped for `<host>/gh`: `/gh/o/r/pull/12` (+ `/files`,
 * `/commits`, `/review`, as GitHub's), `/gh/o/r/compare/main...feat/x`
 * (without a base, `/gh/o/r/compare/feat/x`; a fork's head as
 * `main...ana:feat`) and `/gh/o/r/commits/feat/x`. A compare's or a ref's
 * view is not GitHub's, so it is a query parameter, `?view=commits` (a
 * compare's default is files) or `?view=files` (a ref's default is
 * commits), never written for the default: the path is GitHub's grammar
 * alone. `parseSubjectPath` reads every one back.
 */
export function subjectPath<S extends Subject>(subject: S, view?: SubjectView<S['kind']>): string;
export function subjectPath(subject: Subject, view?: SubjectView): string {
  const repo = `${GH}/${subject.owner}/${subject.repo}`;
  switch (subject.kind) {
    case 'pull':
      return prPath(subject.owner, subject.repo, subject.number, (view as PrTab | undefined) ?? '');
    case 'compare': {
      const head = compareHead({ ...subject, head: refPath(subject.head) });
      return `${repo}/compare/${subject.base === null ? '' : refPath(subject.base) + '...'}${head}${view === 'commits' ? `?${VIEW_PARAM}=commits` : ''}`;
    }
    case 'ref': {
      // The ref as GitHub writes it; a path apart (`?under=`), since GitHub's `commits/<ref>/<path>` says nothing of
      // where the ref ends, and is resolved longest ref first (with a branch `main/src`, that branch's history).
      const q = new URLSearchParams();
      if (subject.path !== null) q.set(UNDER_PARAM, subject.path);
      if (view === 'files') q.set(VIEW_PARAM, 'files');
      return `${repo}/commits/${refPath(subject.ref)}${q.size ? '?' + q : ''}`;
    }
  }
}

/** A GitHub owner or repository name (subject.ts `isGitHubName`). */
const isName = isGitHubName;

/** A compare's head as GitHub writes it (`feat`, `ana:feat`, `ana:fork:feat`), or null when it names no branch. */
function headOf(text: string): Pick<CompareSubject, 'head' | 'headRepo'> | null {
  const parts = text.split(':');
  const head = parts[parts.length - 1];
  if (parts.length > 3 || !isRefName(head)) return null;
  if (parts.length === 1) return { head, headRepo: null };
  const [owner, repo] = parts;
  if (!isName(owner) || (parts.length === 3 && !isName(repo))) return null;
  return { head, headRepo: { owner, repo: parts.length === 3 ? repo : null } };
}

/**
 * The subject and view a path (and its query, `search`) names, or null
 * when it names none. The path is read as GitHub reads it, so a GitHub URL
 * with its host swapped means here what it meant there.
 *
 * Each path segment is percent-decoded on its own, for every kind, so an
 * encoded owner, repository or branch reads alike everywhere; owners and
 * repositories must then be GitHub names. Branch names may hold `/` (their
 * segments are joined again), and an encoded `%2F` is a slash inside one.
 *
 * A compare's `base...head` may be written `base..head` (GitHub accepts it,
 * and it means the same: the diff from the merge base); without a base it
 * is the repository's default branch. Git forbids `..` in a name, so the
 * first `...` (else `..`) splits base from head, and `:` in the head names
 * its fork (`ana:feat`, `ana:fork:feat`). Both must be names git takes
 * (`isRefName`).
 *
 * A compare's ends may be branch or tag names or commit SHAs; relative
 * revisions (`main@{1day}`, `96d29b7^`, `HEAD~2`), which GitHub's compare
 * also takes, are refused deliberately: they name different commits over
 * time, so they can be no subject's key or grant (`isRefName`).
 *
 * A ref's path is GitHub's `commits/<ref>[/<path>]`; where the ref ends is
 * not knowable from the path, so it is read as a `RefLocator` holding the
 * remainder whole, for the ref subject's loader to resolve (see
 * `RefLocator`). Its first component must be a name `isRefName` takes, or
 * `HEAD` (the default branch).
 *
 * The view of a compare or a ref is the query's `view` (`files` or
 * `commits`; absent, its default); another value names nothing. A pull
 * request's views are its path's, as GitHub's.
 */
export function parseSubjectPath(pathname: string, search = ''): Located | null {
  const raw = pathname.split('/');
  if (raw[0] !== '') return null;
  raw.shift();
  // One trailing slash is allowed; any other empty segment names nothing.
  if (raw.length && raw[raw.length - 1] === '') raw.pop();
  const seg: string[] = [];
  for (const r of raw) {
    if (r === '') return null;
    try {
      seg.push(decodeURIComponent(r));
    } catch {
      return null;
    }
  }
  const [gh, owner, repo, kind, ...rest] = seg;
  if ('/' + gh !== GH || !owner || !repo || !isName(owner) || !isName(repo) || !rest.length) return null;
  if (kind === 'pull') {
    const [n, tab] = rest;
    if (rest.length > 2 || !/^\d+$/.test(n)) return null;
    const number = Number(n);
    if (!Number.isSafeInteger(number) || number <= 0) return null;
    // `/drafts` and `/publish` are other URLs of the Review view (see `formerAlias`).
    const prView: PrTab | null =
      tab === undefined ? 'overview' : tab === 'files' || tab === 'changes' ? 'files' : tab === 'commits' ? 'commits' : tab === 'review' || tab === 'drafts' || tab === 'publish' ? 'review' : null;
    return prView && { subject: { kind: 'pull', owner, repo, number }, view: prView };
  }
  const view = new URLSearchParams(search).get(VIEW_PARAM);
  if (view !== null && view !== 'files' && view !== 'commits') return null;
  if (kind === 'compare') {
    const spec = rest.join('/');
    const three = spec.indexOf('...');
    const cut = three >= 0 ? three : spec.indexOf('..');
    const base = cut < 0 ? null : spec.slice(0, cut);
    const head = headOf(cut < 0 ? spec : spec.slice(cut + (three >= 0 ? 3 : 2)));
    if (!head || (base !== null && !isRefName(base))) return null;
    const subject: CompareSubject = { kind: 'compare', owner, repo, base, ...head };
    return { subject, view: view ?? 'files' };
  }
  if (kind === 'commits') {
    // `HEAD`, GitHub's name for the default branch, only as the whole first component.
    if (rest[0] !== 'HEAD' && !isRefName(rest[0])) return null;
    const spec = rest.join('/');
    // `?under=` says where the ref ends: the spec is the ref whole (`subjectPath`).
    const path = new URLSearchParams(search).get(UNDER_PARAM);
    if (path === null) return { locator: { owner, repo, spec }, view: view ?? 'commits' };
    if (!isRefPath(path) || (spec !== 'HEAD' && !isRefName(spec))) return null;
    const locator: RefLocator = { owner, repo, spec, path };
    return { locator, view: view ?? 'commits' };
  }
  return null;
}

/**
 * The path of the compare of `head` (as GitHub writes a head: `feat`,
 * `ana:feat`, `ana:fork:feat`) with `base` (empty: the default branch) in
 * `owner/repo`, as typed into a form, or null when they name no compare
 * (`parseSubjectPath`'s rules: names git takes, no relative revisions).
 */
export function comparePath(owner: string, repo: string, base: string, head: string): string | null {
  const b = base.trim(), h = head.trim();
  if (!h || b.includes('...') || h.includes('...')) return null;
  const enc = (ref: string) => encodeURIComponent(ref).replace(/%2F/gi, '/').replace(/%3A/gi, ':');
  const at = parseSubjectPath(`${GH}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/compare/${b ? enc(b) + '...' : ''}${enc(h)}`);
  return at && 'subject' in at && at.subject.kind === 'compare' ? subjectPath(at.subject) : null;
}

/**
 * The path of the history `spec` names in `owner/repo` (a branch, tag or
 * commit, optionally followed by a path in it; empty, the default
 * branch), as typed into a form, or null when it names none
 * (`parseSubjectPath`'s rules for `commits/<spec>`). The page resolves it.
 */
export function historyPath(owner: string, repo: string, spec: string): string | null {
  const s = spec.trim().replace(/^\/+|\/+$/g, '') || 'HEAD';
  const at = parseSubjectPath(`${GH}/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/commits/${s.split('/').map(encodeURIComponent).join('/')}`);
  return at && 'locator' in at ? locatorPath({ owner, repo, spec: at.locator.spec }) : null;
}

/**
 * A history's address before its page resolves it: `commits/<spec>` as
 * GitHub writes a ref (its slashes kept), its path apart (`?under=`) when
 * the locator says where the ref ends, and its view off the default
 * (`?view=files`), as `subjectPath` writes a resolved one. The router reads
 * it back as the same locator and view.
 */
export function locatorPath(locator: RefLocator, view: SubjectViews['ref'] = 'commits'): string {
  const q = new URLSearchParams();
  if (locator.path !== undefined) q.set(UNDER_PARAM, locator.path);
  if (view === 'files') q.set(VIEW_PARAM, 'files');
  return `${GH}/${locator.owner}/${locator.repo}/commits/${refPath(locator.spec)}${q.size ? '?' + q : ''}`;
}

/** True when an anchor click should be handled by the SPA router. */
export function isRoutableHref(href: string, origin: string): boolean {
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return false;
  }
  if (url.origin !== origin) return false;
  return !isServerPath(url.pathname);
}

/** Same-origin path check for `return_to` style parameters. */
export function isSafeReturnPath(p: string): boolean {
  return p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/\\');
}
